import { ElementType, FC } from 'react';
import { Box, BoxProps, SvgIconProps, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';

type IconContainerSize = 'small' | 'medium' | 'large' | number;

interface IconContainerProps {
    icon: ElementType<SvgIconProps>;
    color?: 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
    size?: IconContainerSize;
    sx?: BoxProps['sx'];
    'data-testid'?: string;
}

const sizeMap: Record<Exclude<IconContainerSize, number>, number> = {
    small: 28,
    medium: 36,
    large: 48,
};

// One size smaller (size = medium then fontSize = small)
const fontSizeMap: Record<Exclude<IconContainerSize, number>, SvgIconProps['fontSize']> = {
    small: 'small',
    medium: 'small',
    large: 'medium',
};

const IconContainer: FC<IconContainerProps> = ({
    icon: Icon,
    color = 'primary',
    size = 'medium',
    sx = {},
    'data-testid': testId,
}) => {
    const theme = useTheme();
    const baseColor = theme.palette[color].main;

    const dimension = typeof size === 'number' ? size : sizeMap[size];
    const iconFontSize: SvgIconProps['fontSize'] = typeof size === 'number' ? 'inherit' : fontSizeMap[size];

    return (
        <Box
            data-testid={testId}
            sx={{
                width: dimension,
                height: dimension,
                borderRadius: 1,
                backgroundColor: alpha(baseColor, 0.08), // Derived from: primary/selected
                border: `2px solid ${alpha(baseColor, 0.5)}`, // Derived from: primary/outlinedBorder
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                ...sx,
            }}
        >
            <Icon sx={{ color: `${color}.main` }} fontSize={iconFontSize} />
        </Box>
    );
};

export type { IconContainerProps };
export default IconContainer;
